import { MissionsHistory } from '../features/history/PageHistory.tsx';
import { Bug, Columns3, FileText, Inbox, ListTree, Plus, Rocket, ScanSearch, Sparkles, Table2, Target, Upload } from '@orgus/ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { failedOnModel, statusLabel } from '@orgus/protocol';
import type { BacklogItem, BacklogViewKind, Id, ItemStatus } from '@orgus/protocol';
import { Button, EmptyState, ErrorState, SplitButton, toast, useNow } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../lib/api.ts';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { PageActions } from '../components/shell/TopBar.tsx';
import { useRegisterCommands } from '../lib/commands.ts';
import type { Command } from '../lib/commands.ts';
import { useProjects } from '../lib/store.ts';
import { usePageKeys } from '../features/agents/usePageKeys.ts';
import { refreshMissions, updateItem, useMissionRows, useMissionsState } from '../features/missions/data.ts';
import { ItemDialog } from '../features/missions/ItemDialog.tsx';
import type { ItemDraft } from '../features/missions/ItemDialog.tsx';
import { ItemDrawer } from '../features/missions/ItemDrawer.tsx';
import type { AiItemTask } from '../features/missions/ItemDrawer.tsx';
import type { MissionRow } from '../features/missions/model.ts';
import { MissionDetail } from '../features/missions/MissionDetail.tsx';
import { missionsGlance, waitingOutsideInbox } from '../features/missions/glance.ts';
import { useNeedsYouState } from '../features/asks/needsYou.ts';
import { MissionsGlance } from '../features/missions/MissionsGlance.tsx';
import { PausedBanner, PauseMissionsButton } from '../features/missions/PauseControl.tsx';
import { HomeSkeleton, MissionsHome, readHomeParams } from '../features/missions/MissionsHome.tsx';
import { StartMissionDialog } from '../features/missions/StartMissionDialog.tsx';
import type { StartTarget } from '../features/missions/StartMissionDialog.tsx';
import { AiJobsMenu } from '../features/requirements/AiJobs.tsx';
import { ImportDialog } from '../features/requirements/ImportDialog.tsx';
import { ProposalReview } from '../features/requirements/ProposalReview.tsx';
import { startAiTask, useAiJobs } from '../features/requirements/data.ts';
import { qualityIndex } from '../features/requirements/model.ts';

const AI_TASK_DONE: Record<AiItemTask, string> = {
  improve: 'Improving',
  criteria: 'Writing acceptance criteria for',
  split: 'Splitting',
  estimate: 'Estimating',
};

/**
 * Missions: requirements (epics, features, stories, tasks, defects) and the missions that work them.
 *   /missions                      backlog: ?view=tree|board|table|doc|defects, filters (?kind=, ?status=, ?q=, ...),
 *                                  ?item=<id> opens the item, ?proposal=<id> opens an AI proposal for review
 *   /missions?tab=missions         the missions list (?scope=active|closed|all)
 *   /missions?tab=review           AI proposals, suggested defects and AI jobs
 *   /missions/<msn_id>             one mission
 */
export default function Missions() {
  const navigate = useNavigate();
  const { '*': rest = '' } = useParams();
  const missionId = rest.split('/')[0] || null;
  const [params, setParams] = useSearchParams();
  const home = readHomeParams(params);
  const now = useNow(5000);
  const projects = useProjects();
  const { state, phase } = useMissionsState();
  const rows = useMissionRows(state, now);
  const { state: aiState } = useAiJobs();
  const jobs = useMemo(() => aiState?.jobs ?? [], [aiState]);

  const [itemDialog, setItemDialog] = useState<ItemDraft | null>(null);
  const [importing, setImporting] = useState(false);
  const [startTarget, setStartTarget] = useState<StartTarget | null>(null);
  /** What an action started from this page failed with: shown where it ran (the drawer or the page), never a toast. */
  const [actionError, setActionError] = useState<UiError | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  /** Jobs started from this page: when one finishes with a proposal, its review opens. */
  const watching = useRef(new Set<Id>());

  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const projectName = useCallback((id: Id) => projectById.get(id)?.name ?? 'Unknown project', [projectById]);
  const items = state?.items ?? [];
  const links = state?.links ?? [];
  const issues = useMemo(() => qualityIndex(items, links), [items, links]);
  const reviewModes = state?.review;
  const glance = useMemo(() => missionsGlance(rows, items, home.projectId, now, 14, reviewModes ?? {}, { proposals: state?.proposals, signals: state?.signals }), [rows, items, home.projectId, now, reviewModes, state?.proposals, state?.signals]);
  const needsYou = useNeedsYouState();
  const outsideInbox = useMemo(() => waitingOutsideInbox(rows, needsYou.bySession, home.projectId), [rows, needsYou.bySession, home.projectId]);
  const openItem = items.find((i) => i.id === home.itemId);
  const proposal = (state?.proposals ?? []).find((p) => p.id === home.proposalId) ?? null;

  const setParam = useCallback(
    (k: string, v: string | null, push = false) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          if (v) n.set(k, v);
          else n.delete(k);
          return n;
        },
        { replace: !push },
      ),
    [setParams],
  );
  const setItemParam = useCallback((id: Id | null) => setParam('item', id, !!id), [setParam]);
  const openReview = useCallback((id: Id) => setParams((p) => {
    const n = new URLSearchParams(p);
    n.set('proposal', id);
    n.delete('item');
    return n;
  }), [setParams]);

  useEffect(() => {
    for (const j of jobs) {
      if (!watching.current.has(j.id)) continue;
      if (j.state === 'done' && j.proposalId) {
        watching.current.delete(j.id);
        if (!missionId) openReview(j.proposalId);
      } else if (j.state === 'failed' || j.state === 'cancelled') watching.current.delete(j.id);
    }
  }, [jobs, missionId, openReview]);
  const watch = (jobId: Id) => watching.current.add(jobId);

  const newItem = (draft: ItemDraft = {}) => setItemDialog({ projectId: home.projectId ?? undefined, ...draft });
  const startItem = (item: BacklogItem) => setStartTarget({ item });
  const startMany = (list: BacklogItem[]) => setStartTarget(list.length === 1 ? { item: list[0] } : { items: list });
  const startSequence = (item: BacklogItem) => setStartTarget({ sequence: item });
  /** Retry a failed or stopped mission like its last start: another session, or a new mission once it is closed. */
  const retryMission = (row: MissionRow) => {
    const launch = row.mission.launches[row.mission.launches.length - 1] ?? null;
    const retryFrom = { launch, failedOnModel: row.view.sessions.some((s) => s.state === 'failed' && !!s.session && failedOnModel(s.session)) };
    setStartTarget(row.mission.closed ? { item: row.item, retryFrom } : { mission: row.mission, retryFrom });
  };
  const newMission = () => setStartTarget({ projectId: home.projectId });
  const move = async (item: BacklogItem, status: ItemStatus) => {
    try {
      await updateItem(item.id, { status });
      toast.success(`Moved to ${statusLabel(item.kind, status)}`, `${item.key} ${item.title}`);
    } catch (e) {
      setActionError(toUiError(e));
    }
  };
  const aiTask = async (task: AiItemTask, item: BacklogItem) => {
    try {
      const r = await startAiTask({ task, itemId: item.id });
      watch(r.job.id);
      toast.success(`${AI_TASK_DONE[task]} ${item.key}`, 'The proposal opens for review when it is ready.');
    } catch (e) {
      setActionError(toUiError(e));
    }
  };

  const mission = missionId ? state?.missions.find((m) => m.id === missionId) : undefined;
  const missionRow = mission ? rows.find((r) => r.mission.id === mission.id) : undefined;
  const ready = phase === 'ready' && !!state;
  // ?start=1&project=&title=&goal= (a stuck landing's Needs you row, ORGU-83) opens Start a
  // mission once, prefilled; the person reviews and sends it.
  const startAsked = params.get('start') === '1';
  useEffect(() => {
    if (!startAsked || !ready) return;
    setStartTarget({
      projectId: params.get('project') || home.projectId,
      draft: { title: params.get('title') ?? '', goal: params.get('goal') ?? '' },
    });
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        for (const k of ['start', 'project', 'title', 'goal']) n.delete(k);
        return n;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAsked, ready]);
  // ?retry=1 (from the Inbox's timed-out request) opens Retry for the mission once.
  const retryAsked = params.get('retry') === '1';
  useEffect(() => {
    if (!retryAsked || !missionRow) return;
    retryMission(missionRow);
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.delete('retry');
        return n;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryAsked, !!missionRow]);
  const setView = (v: BacklogViewKind) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.delete('tab');
        if (v === 'tree') n.delete('view');
        else n.set('view', v);
        n.delete('saved');
        return n;
      },
      { replace: true },
    );

  const cmds = useMemo<Command[]>(() => {
    if (!ready) return [];
    const list: Command[] = [
      { id: 'missions.item.new', title: 'New item', subtitle: 'An epic, feature, story, task, defect, spike or chore', group: 'Requirements', icon: Plus, shortcut: ['c'], order: 10, perform: () => newItem() },
      { id: 'missions.item.ai', title: 'Write an item with AI', subtitle: 'From one line or a screenshot; you review it first', group: 'Requirements', icon: Sparkles, shortcut: ['a'], order: 11, perform: () => newItem({ ai: true }) },
      { id: 'missions.import', title: 'Import a document', subtitle: 'Markdown, Word or PDF into epics and stories', group: 'Requirements', icon: Upload, shortcut: ['i'], order: 12, perform: () => setImporting(true) },
      { id: 'missions.defect', title: 'Report a defect', group: 'Requirements', icon: Bug, order: 13, perform: () => newItem({ kind: 'defect' }) },
      { id: 'missions.new', title: 'New mission', subtitle: 'Give Claude Code a goal on one of your machines', group: 'Missions', icon: Rocket, order: 14, perform: newMission },
      { id: 'missions.tree', title: 'Show the hierarchy', group: 'Requirements', icon: ListTree, shortcut: ['t'], order: 20, perform: () => setView('tree') },
      { id: 'missions.board', title: 'Show the board', group: 'Requirements', icon: Columns3, shortcut: ['b'], order: 21, perform: () => setView('board') },
      { id: 'missions.table', title: 'Show the table', group: 'Requirements', icon: Table2, shortcut: ['l'], order: 22, perform: () => setView('table') },
      { id: 'missions.doc', title: 'Read as a document', group: 'Requirements', icon: FileText, shortcut: ['d'], order: 23, perform: () => setView('doc') },
      { id: 'missions.defects', title: 'Show defects', group: 'Requirements', icon: Bug, shortcut: ['f'], order: 24, perform: () => setView('defects') },
      { id: 'missions.review', title: 'Open the review queue', subtitle: 'AI proposals, suggested defects, AI jobs', group: 'Requirements', icon: Inbox, order: 25, perform: () => setParam('tab', 'review') },
      { id: 'missions.all', title: 'Show missions', group: 'Missions', icon: Target, order: 26, perform: () => navigate('/missions?tab=missions') },
    ];
    if (home.projectId) {
      list.push({ id: 'missions.dedupe', title: 'Find duplicates with AI', subtitle: projectName(home.projectId), group: 'Requirements', icon: ScanSearch, order: 30, perform: () => void startAiTask({ task: 'dedupe', projectId: home.projectId! }).then((r) => watch(r.job.id), (e) => setActionError(toUiError(e))) });
    }
    for (const r of rows.filter((x) => x.view.state === 'waiting' || x.view.state === 'running').slice(0, 15)) {
      list.push({ id: `missions.open:${r.mission.id}`, title: `Open mission: ${r.mission.title}`, subtitle: projectName(r.mission.projectId), group: 'Missions', icon: Target, keywords: ['mission', r.mission.id], order: 40, perform: () => navigate(`/missions/${r.mission.id}`) });
    }
    for (const it of items.filter((i) => i.status !== 'dropped' && i.status !== 'done').slice(0, 200)) {
      list.push({ id: `missions.item:${it.id}`, title: `${it.key} ${it.title}`, subtitle: projectName(it.projectId), group: 'Items', icon: Target, keywords: [it.key, it.kind], order: 60, perform: () => navigate(`/missions?item=${encodeURIComponent(it.id)}`) });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, rows, items, projectName, home.projectId]);
  useRegisterCommands(cmds, [cmds]);

  usePageKeys(
    missionId || openItem || proposal
      ? {}
      : {
          c: () => ready && newItem(),
          a: () => ready && newItem({ ai: true }),
          i: () => ready && setImporting(true),
          t: () => setView('tree'),
          b: () => setView('board'),
          l: () => setView('table'),
          d: () => setView('doc'),
          f: () => setView('defects'),
          '/': () => searchRef.current?.focus(),
          '1': () => setParam('tab', null),
          '2': () => setParam('tab', 'missions'),
          '3': () => setParam('tab', 'review'),
        },
  );

  const unavailable = (
    <div className="rounded-card border border-border bg-surface">
      <ErrorState
        error={{ what: 'Missions are not available on this hub.', why: 'The hub did not report a missions module, so it is likely an older version.', next: 'Restart the hub on the current Orgus version, then reload.' }}
        onRetry={() => refreshMissions()}
      />
    </div>
  );

  let body;
  if (phase === 'unavailable') body = unavailable;
  else if (!ready) body = <HomeSkeleton />;
  else if (missionId) {
    body =
      mission && missionRow ? (
        <MissionDetail mission={mission} view={missionRow.view} item={missionRow.item} projectName={projectName} onStartSession={(retryFrom) => setStartTarget({ mission, retryFrom })} />
      ) : (
        <div className="rounded-card border border-border bg-surface">
          <EmptyState
            size="page"
            icon={<Target />}
            title="That mission is not here"
            description="It may have been deleted, or the link points at another hub."
            action={
              <Button variant="primary" onClick={() => navigate('/missions?tab=missions')}>
                Show all missions
              </Button>
            }
          />
        </div>
      );
  } else {
    body = (
      <div className="flex flex-col gap-6">
        {actionError && !openItem && <ErrorState variant="inline" error={actionError} action={<Button variant="ghost" size="sm" onClick={() => setActionError(null)}>Dismiss</Button>} />}
        {state!.items.length + state!.missions.length > 0 && (
          <MissionsGlance
            g={glance}
            now={now}
            outsideInbox={outsideInbox}
            onAnswer={() => glance.firstWaitingId && navigate(`/missions/${encodeURIComponent(glance.firstWaitingId)}`)}
            onReview={() => setParam('tab', 'review')}
            onMissions={() => setParams((p) => {
              const n = new URLSearchParams(p);
              n.set('tab', 'missions');
              n.delete('scope');
              return n;
            }, { replace: true })}
            onFinished={() => setParams((p) => {
              const n = new URLSearchParams(p);
              n.set('tab', 'missions');
              n.set('scope', 'closed');
              return n;
            }, { replace: true })}
          />
        )}
        {/* With no project the history is empty by definition; the empty state's add-project action leads. */}
        {/* QA-36: below 640 px the backlog comes straight after the counts; the charts are a desktop extra. */}
        {projects.length > 0 && (
          <div className="max-sm:hidden">
            <MissionsHistory />
          </div>
        )}
        <MissionsHome
          state={state!}
          rows={rows}
          projects={projects}
          projectName={projectName}
          issues={issues}
          jobs={jobs}
          onOpenItem={(id) => setItemParam(id)}
          onNewItem={(d) => newItem(d ?? {})}
          onImport={() => setImporting(true)}
          onStartItem={startItem}
          onStartMany={startMany}
          onRetry={retryMission}
          onStartSequence={startSequence}
          onNewMission={newMission}
          onMove={(i, s) => void move(i, s)}
          onReview={openReview}
          onAiStarted={watch}
          searchRef={searchRef}
        />
      </div>
    );
  }

  return (
    <PageBody wide>
      {!missionId && ready && (
        <PageActions>
          {projects.length > 0 && <PauseMissionsButton />}
          <AiJobsMenu jobs={jobs} onReview={openReview} onOpenAll={() => setParam('tab', 'review')} />
          {/* One primary: New item. Writing with AI, importing and the rest fold into its menu. */}
          <SplitButton
            label="New item"
            icon={<Plus />}
            hint="Add an epic, feature, story, task or defect"
            shortcut={['c']}
            onClick={() => newItem()}
            menuLabel="More ways to add"
            items={[
              { id: 'ai', label: 'Write with AI', icon: <Sparkles />, shortcut: ['a'], onSelect: () => newItem({ ai: true }) },
              { id: 'import', label: 'Import a document', icon: <Upload />, shortcut: ['i'], onSelect: () => setImporting(true) },
              { id: 'defect', label: 'Report a defect', icon: <Bug />, onSelect: () => newItem({ kind: 'defect' }) },
              { id: 'mission', label: 'New mission', icon: <Rocket />, onSelect: newMission, separated: true },
            ]}
          />
        </PageActions>
      )}
      {!missionId && ready && <PausedBanner />}
      <DataGate skeleton={<HomeSkeleton />}>{body}</DataGate>

      {ready && (
        <>
          <ItemDrawer
            item={missionId || proposal ? undefined : openItem}
            items={items}
            links={links}
            rows={rows}
            issues={issues}
            jobs={jobs}
            projectName={projectName}
            onClose={() => setItemParam(null)}
            onOpenItem={(id) => setItemParam(id)}
            onOpenMission={(id) => navigate(`/missions/${encodeURIComponent(id)}`)}
            onStart={startItem}
            onRetry={retryMission}
            onStartSequence={startSequence}
            onAddChild={(parent) => newItem({ projectId: parent.projectId, parentId: parent.id, kind: parent.kind === 'epic' ? 'feature' : parent.kind === 'feature' ? 'story' : 'task' })}
            onAiTask={(t, it) => void aiTask(t, it)}
            onReviewProposal={openReview}
            error={actionError}
            onDismissError={() => setActionError(null)}
          />
          <ProposalReview
            proposal={proposal}
            items={items}
            open={!!proposal && !missionId}
            onOpenChange={(o) => !o && setParam('proposal', null)}
            onCreated={(ids) => {
              if (ids.length === 1) setParams((p) => {
                const n = new URLSearchParams(p);
                n.delete('proposal');
                n.set('item', ids[0]!);
                return n;
              });
            }}
          />
          <ItemDialog open={!!itemDialog} onOpenChange={(o) => !o && setItemDialog(null)} draft={itemDialog ?? {}} projects={projects} items={items} onCreated={(it, problem) => {
              setItemParam(it.id);
              if (problem) setActionError(problem);
            }} onAiStarted={watch} />
          <ImportDialog open={importing} onOpenChange={setImporting} projects={projects} items={items} projectId={home.projectId} onStarted={watch} />
          <StartMissionDialog
            open={!!startTarget}
            onOpenChange={(o) => !o && setStartTarget(null)}
            target={startTarget ?? {}}
            items={items}
            links={links}
            onStarted={(id) => {
              if (id !== missionId) navigate(`/missions/${encodeURIComponent(id)}`);
            }}
            onSequenceStarted={(rootId) => setItemParam(rootId)}
            onManyStarted={() => navigate('/missions?tab=missions')}
          />
        </>
      )}
    </PageBody>
  );
}
